import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";
import Input from "@client.components/Input";
import UserNotificationTypes from "@client.enums/userNotificationTypes";
import Dropdown from "@client.components/Dropdown";
import { useTranslation } from "react-i18next";
import RecurringTypes from "@client.enums/recurringTypes";
import InvestmentTypes from "@client.enums/investmentTypes";
import { makeNotificationSettings } from "@client.pages/Settings/reducer";
import Loader from "@client.components/Loader";

function useDebouncedCallback(callback, delay) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useMemo(
    () =>
      debounce((...args) => {
        callbackRef.current(...args);
      }, delay),
    [delay]
  );
}

const SettingInput = ({
  notificationType,
  onSave,
  isInput,
  suffix,
  inputClassName,
}) => {
  const { t } = useTranslation();
  const notificationSettings = useSelector(makeNotificationSettings);

  const notification = notificationSettings.data.find(
    (n) => n.type === notificationType
  );

  const { type, isEnabled, settingsJson, value } = notification ?? {};

  const [localValue, setLocalValue] = useState(value ?? "");

  useEffect(() => {
    // Keep local value in sync with Redux state
    setLocalValue(value ?? "");
  }, [value]);

  const debouncedSave = useDebouncedCallback((parsedValue) => {
    onSave({ ...notification, value: parsedValue });
  }, 500);

  const handleInputChange = (inputValue) => {
    // Allow clearing field
    setLocalValue(inputValue);

    // Check for valid number (or empty string, for in-progress typing)
    if (inputValue === "") return;

    const parsed = Number(inputValue);
    if (!Number.isNaN(parsed) && parsed >= 1) {
      debouncedSave(parsed);
    }
  };

  const renderInput = () => {
    return (
      <Input
        type="number"
        value={localValue}
        inputClassName={inputClassName}
        onChange={handleInputChange}
        disabled={!isEnabled}
        optional={{
          step: 1,
          min: 1,
          max: 100,
        }}
        suffix={suffix}
      />
    );
  };

  const renderSettingsDropdown = () => {
    switch (type) {
      case UserNotificationTypes.ScheduleRecurringMeeting:
      case UserNotificationTypes.EmployeeOverspendExternal:
      case UserNotificationTypes.EmployeeOverspendInternal: {
        const dataSource = [
          { label: "weekly", value: RecurringTypes.Weekly },
          { label: "monthly", value: RecurringTypes.Monthly },
          { label: "quaterly", value: RecurringTypes.Quaterly },
        ];

        if (type === UserNotificationTypes.ScheduleRecurringMeeting) {
          dataSource.unshift({
            label: "biWeekly",
            value: RecurringTypes.BiWeekly,
          });
          dataSource.push({
            label: "all",
            value: RecurringTypes.All,
          });
        }

        return (
          <Dropdown
            items={dataSource.map((m) => ({
              ...m,
              label: t(
                `components.Settings.InsightsSettings.RecurringTypes.${m.label}`
              ),
            }))}
            valueKey="value"
            labelKey="label"
            disabled={!isEnabled}
            defaultValue={settingsJson?.recurringType}
            onPrepareValue={(val) => parseInt(val)}
            onSelect={(v) =>
              onSave({
                ...notification,
                settingsJson: { recurringType: v },
              })
            }
          />
        );
      }

      case UserNotificationTypes.ScheduleHighInvestmentMeeting:
        return (
          <Dropdown
            items={[
              { label: "score", value: InvestmentTypes.Score },
              { label: "amount", value: InvestmentTypes.Amount },
            ].map((m) => ({
              ...m,
              label: t(
                `components.Settings.InsightsSettings.InvestmentTypes.${m.label}`
              ),
            }))}
            valueKey="value"
            labelKey="label"
            disabled={!isEnabled}
            defaultValue={settingsJson?.investmentType}
            onPrepareValue={(val) => parseInt(val)}
            onSelect={(v) =>
              onSave({
                ...notification,
                settingsJson: {
                  ...notification.settingsJson,
                  investmentType: v,
                },
              })
            }
          />
        );

      default:
        return <div className="element" />;
    }
  };

  if (notificationSettings.load || !notification) {
    return <Loader loading />;
  }

  return isInput ? renderInput() : renderSettingsDropdown();
};

SettingInput.propTypes = {
  notificationType: PropTypes.number,
  onSave: PropTypes.func,
  isInput: PropTypes.bool,
  suffix: PropTypes.string,
  inputClassName: PropTypes.string,
};

export default SettingInput;
